import { useState, useEffect, useMemo, useRef } from "react";
import { Slider } from "@/components/ui/slider";
import { cn } from "@/lib/utils";
import { TimeInForceSelector } from "./time-in-force-selector";
import { getMaxPositionFromConfig, type LeverageConfig } from "@/features/trading-settings/types";
import { X } from "lucide-react";

type TimeInForce = "GTC" | "IOC" | "ALO";

// Maximum characters allowed in price inputs
const MAX_PRICE_LENGTH = 12;

// Track which input is actively being edited
type ActiveInput = "tpPrice" | "slPrice" | null;

// Clean input component matching modal style
const TpSlInput = ({
  value,
  onChange,
  onClear,
  onFocus,
  onBlur,
  placeholder,
  suffix,
}: {
  value: string;
  onChange: (value: string) => void;
  onClear: () => void;
  onFocus?: () => void;
  onBlur?: () => void;
  placeholder: string;
  suffix: string;
}) => (
  <div
    className={cn(
      "flex items-center rounded-md border bg-transparent transition-all duration-200 overflow-hidden",
      "border-muted-foreground/25 hover:border-muted-foreground/80",
      "focus-within:border-foreground/70 dark:focus-within:border-white/80"
    )}
  >
    <input
      type="text"
      inputMode="decimal"
      placeholder={placeholder}
      value={value}
      onChange={(e) => {
        const cleaned = e.target.value.replace(/[^0-9.]/g, "");
        // Only allow valid decimal format (prevents multiple dots)
        if (cleaned === "" || /^\d*\.?\d*$/.test(cleaned)) {
          if (cleaned.length <= MAX_PRICE_LENGTH) onChange(cleaned);
        }
      }}
      onFocus={onFocus}
      onBlur={onBlur}
      className={cn(
        "flex-1 min-w-0 bg-transparent px-2.5 py-2 text-xs text-foreground outline-none placeholder:text-muted-foreground",
        "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
      )}
    />
    {value && (
      <button
        type="button"
        onClick={onClear}
        className="px-1 text-muted-foreground/50 hover:text-foreground transition-colors flex-shrink-0"
      >
        <X className="size-3" />
      </button>
    )}
    <span className="pr-2.5 text-xs text-muted-foreground flex-shrink-0">{suffix}</span>
  </div>
);

type ValuePercentageSliderProps = {
  availableBalance: number;
  currentValue: string;
  onValueChange: (value: string) => void;
  quotePrecision: number;
  currentPrice: number;
  leverage: number;
  leverageConfig: LeverageConfig | null;
  valueCurrency: "quote" | "base";
  orderType: "market" | "limit" | "advanced";
  limitPrice?: string; // Limit price for limit orders (used for TP/SL calculations)
  timeInForce: TimeInForce;
  onTimeInForceChange: (tif: TimeInForce) => void;
  reduceOnly?: boolean;
  onReduceOnlyChange?: (enabled: boolean) => void;
  // Take Profit / Stop Loss callbacks
  onTpSlChange?: (takeProfit: string | null, stopLoss: string | null) => void;
};

export const ValuePercentageSlider = ({
  availableBalance,
  currentValue,
  onValueChange,
  quotePrecision,
  currentPrice,
  leverage,
  leverageConfig,
  valueCurrency,
  orderType,
  limitPrice,
  timeInForce,
  onTimeInForceChange,
  reduceOnly = false,
  onReduceOnlyChange,
  onTpSlChange,
}: ValuePercentageSliderProps) => {
  const [percentage, setPercentage] = useState(0);
  const [isSliderActive, setIsSliderActive] = useState(false);
  const [percentageInput, setPercentageInput] = useState("0");
  const [tpslEnabled, setTpslEnabled] = useState(false);
  const [reduceOnlyEnabled, setReduceOnlyEnabled] = useState(reduceOnly);
  
  // TP/SL state
  const [tpPrice, setTpPrice] = useState("");
  const [slPrice, setSlPrice] = useState("");
  
  // Track which input is being actively edited to prevent overwriting
  const activeInputRef = useRef<ActiveInput>(null);

  // Notify parent when TP/SL changes
  useEffect(() => {
    if (onTpSlChange) {
      const tp = tpslEnabled && tpPrice ? tpPrice : null;
      const sl = tpslEnabled && slPrice ? slPrice : null;
      onTpSlChange(tp, sl);
    }
  }, [tpPrice, slPrice, tpslEnabled, onTpSlChange]);

  // Helper function to format number with thousand separators
  const formatWithCommas = (value: string): string => {
    if (!value) return "";
    // Remove existing commas
    const cleanValue = value.replace(/,/g, "");
    // Check if it's a valid number
    if (isNaN(Number(cleanValue))) return value;
    // Split by decimal point
    const parts = cleanValue.split(".");
    // Add commas to integer part
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    // Join back with decimal
    return parts.join(".");
  };

  // Helper function to parse formatted number
  const parseFormattedNumber = (value: string): string => {
    return value.replace(/,/g, "");
  };

  // Helper function to round to "clean" numbers based on magnitude
  // This makes the slider feel nicer with round values instead of uneven decimals
  const roundToCleanNumber = (value: number, maxValue: number): number => {
    if (value <= 0) return 0;
    
    // Determine rounding increment based on the max position size
    let roundingIncrement: number;
    if (maxValue >= 10_000_000) {
      roundingIncrement = 10_000;   // Round to nearest $10,000 for very large positions
    } else if (maxValue >= 1_000_000) {
      roundingIncrement = 1_000;    // Round to nearest $1,000 for large positions
    } else if (maxValue >= 100_000) {
      roundingIncrement = 100;      // Round to nearest $100 for medium positions
    } else if (maxValue >= 10_000) {
      roundingIncrement = 10;       // Round to nearest $10 for smaller positions
    } else {
      roundingIncrement = 1;        // Round to nearest $1 for small positions
    }
    
    return Math.round(value / roundingIncrement) * roundingIncrement;
  };

  // Calculate effective max position value (minimum of balance-based max and leverage tier max)
  const maxPositionValue = useMemo(() => {
    const balanceBasedMax = availableBalance * leverage;
    const leverageTierMax = getMaxPositionFromConfig(leverage, leverageConfig);
    return Math.min(balanceBasedMax, leverageTierMax);
  }, [availableBalance, leverage, leverageConfig]);

  // Calculate percentage from current value (when user types manually)
  useEffect(() => {
    if (!isSliderActive && maxPositionValue > 0) {
      const currentVal = parseFloat(currentValue) || 0;
      
      // Convert current value to USDC if in base currency
      const currentValueInUSDC = valueCurrency === "base" 
        ? currentVal * currentPrice 
        : currentVal;
      
      const calculatedPercentage = Math.min(100, (currentValueInUSDC / maxPositionValue) * 100);
      setPercentage(Math.round(calculatedPercentage));
    }
  }, [currentValue, maxPositionValue, isSliderActive, valueCurrency, currentPrice]);

  // Sync percentageInput with percentage when percentage changes (from slider or external)
  useEffect(() => {
    setPercentageInput(String(percentage));
  }, [percentage]);

  // Handle percentage input change - live update
  const handlePercentageInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9]/g, "");
    if (value.length <= 3) {
      setPercentageInput(value);
      // Live update: immediately apply valid values
      const numValue = parseInt(value) || 0;
      if (numValue >= 0 && numValue <= 100) {
        handleSliderChange([numValue]);
      }
    }
  };

  // Handle percentage input blur - apply the value
  const handlePercentageInputBlur = () => {
    let value = parseInt(percentageInput) || 0;
    // Clamp between 0 and 100
    value = Math.min(100, Math.max(0, value));
    setPercentageInput(String(value));
    handleSliderChange([value]);
  };

  // Handle Enter key on percentage input
  const handlePercentageInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handlePercentageInputBlur();
      (e.target as HTMLInputElement).blur();
    }
  };

  // Update value when slider moves
  const handleSliderChange = (value: number[]) => {
    setIsSliderActive(true);
    setPercentage(value[0]);
    
    // Use the effective max position value (respects leverage tier limits)
    const calculatedValueInUSDC = (maxPositionValue * value[0]) / 100;
    
    // Round to clean numbers for better UX (only for USD values)
    const roundedValueInUSDC = valueCurrency === "quote" 
      ? roundToCleanNumber(calculatedValueInUSDC, maxPositionValue)
      : calculatedValueInUSDC;
    
    // Convert to selected currency
    const finalValue = valueCurrency === "base" 
      ? roundedValueInUSDC / currentPrice 
      : roundedValueInUSDC;
    
    onValueChange(finalValue.toFixed(valueCurrency === "base" ? 4 : quotePrecision));
    
    // Reset slider active state after a short delay
    setTimeout(() => setIsSliderActive(false), 100);
  };

  // Handle TP price change
  const handleTpPriceChange = (value: string) => {
    setTpPrice(value);
  };

  // Handle SL price change
  const handleSlPriceChange = (value: string) => {
    setSlPrice(value);
  };

  // Calculate position size in base currency for PnL estimation
  const positionSize = useMemo(() => {
    const orderVal = parseFloat(currentValue) || 0;
    if (orderVal === 0 || currentPrice === 0) return 0;
    
    // Convert to base currency (e.g., BTC amount)
    return valueCurrency === "base" ? orderVal : orderVal / currentPrice;
  }, [currentValue, currentPrice, valueCurrency]);

  // Detect intended side from TP/SL configuration
  // If TP > SL → Long configuration (profit when price goes UP)
  // If TP < SL → Short configuration (profit when price goes DOWN)
  const detectedSide = useMemo((): "long" | "short" | null => {
    const tpPriceNum = parseFloat(parseFormattedNumber(tpPrice)) || 0;
    const slPriceNum = parseFloat(parseFormattedNumber(slPrice)) || 0;
    
    // Need both values to detect side
    if (tpPriceNum > 0 && slPriceNum > 0) {
      return tpPriceNum > slPriceNum ? "long" : "short";
    }
    return null; // Can't determine without both values
  }, [tpPrice, slPrice]);

  // Calculate estimated profit/loss based on TP/SL prices and detected side
  const estimatedPnL = useMemo(() => {
    const tpPriceNum = parseFloat(parseFormattedNumber(tpPrice)) || 0;
    const slPriceNum = parseFloat(parseFormattedNumber(slPrice)) || 0;
    
    // Use limit price as entry price for limit orders, otherwise use current market price
    // This ensures TP/SL calculations reflect the intended entry price for limit orders
    const entryPrice = orderType === "limit" && limitPrice
      ? parseFloat(limitPrice) || currentPrice
      : currentPrice;
    
    // Only calculate if we can detect the side (both TP and SL entered)
    if (!detectedSide || positionSize <= 0) {
      return { estProfit: null, estLoss: null };
    }
    
    // Calculate based on detected side
    let estProfit: number | null = null;
    let estLoss: number | null = null;
    
    if (detectedSide === "long") {
      // For LONG position:
      // Est. Profit = (TP Price - Entry Price) × Position Size (price goes UP)
      // Est. Loss = (Entry Price - SL Price) × Position Size (price goes DOWN)
      estProfit = tpPriceNum > 0 ? (tpPriceNum - entryPrice) * positionSize : null;
      estLoss = slPriceNum > 0 ? (entryPrice - slPriceNum) * positionSize : null;
    } else {
      // For SHORT position:
      // Est. Profit = (Entry Price - TP Price) × Position Size (price goes DOWN)
      // Est. Loss = (SL Price - Entry Price) × Position Size (price goes UP)
      estProfit = tpPriceNum > 0 ? (entryPrice - tpPriceNum) * positionSize : null;
      estLoss = slPriceNum > 0 ? (slPriceNum - entryPrice) * positionSize : null;
    }
    
    return { estProfit, estLoss };
  }, [tpPrice, slPrice, currentPrice, positionSize, orderType, limitPrice, detectedSide]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-3">
          <div className="relative flex-1 px-1">
            {/* Dots first so thumb can sit above them */}
            {[0, 25, 50, 75, 100].map((mark) => {
              const isCurrent = percentage === mark;
              const isFilled = percentage > mark;
              return (
                <button
                  key={mark}
                  type="button"
                  onClick={() => handleSliderChange([mark])}
                  aria-label={`Set to ${mark}%`}
                  className={cn(
                    "absolute top-1/2 -translate-x-1/2 -translate-y-1/2 h-2 w-2 rounded-full transition-all duration-200 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary/40 z-0",
                    isCurrent && "scale-75 opacity-0 pointer-events-none",
                    !isCurrent &&
                      (isFilled
                        ? "bg-primary shadow-sm"
                        : "bg-muted-foreground/50 hover:bg-primary/60")
                  )}
                  style={{ left: `${mark}%` }}
                >
                  <span className="sr-only">{mark}%</span>
                </button>
              );
            })}

            <Slider
              value={[percentage]}
              onValueChange={handleSliderChange}
              max={100}
              step={1}
              className="relative z-10 flex-1 cursor-pointer"
              trackClassName="relative z-0 h-1 w-full rounded-full bg-muted-foreground/25"
              rangeClassName="absolute z-0 h-full rounded-full bg-primary"
              thumbClassName="relative z-20 block h-4 w-4 rounded-full border border-background bg-primary shadow-md transition-transform hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
            />
          </div>

          <div className="shrink-0 rounded-md border border-border px-1.5 py-1 w-[3.5rem] text-center bg-input/30 hover:border-muted-foreground/60 focus-within:border-foreground/70 flex items-center justify-center transition-colors">
            <input
              type="text"
              inputMode="numeric"
              value={percentageInput}
              onChange={handlePercentageInputChange}
              onBlur={handlePercentageInputBlur}
              onKeyDown={handlePercentageInputKeyDown}
              className="w-[1.5rem] bg-transparent text-xs font-semibold text-foreground/90 text-right outline-none"
              aria-label="Percentage"
            />
            <span className="text-xs font-semibold text-foreground/90">%</span>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 text-xs cursor-pointer group">
            <div className="relative">
              <input
                type="checkbox"
                checked={reduceOnlyEnabled}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setReduceOnlyEnabled(checked);
                  onReduceOnlyChange?.(checked);
                  // If Reduce-Only is enabled, disable TP/SL
                  if (checked) {
                    setTpslEnabled(false);
                  }
                }}
                className="appearance-none w-4 h-4 rounded border border-muted-foreground/30 bg-muted checked:bg-muted checked:border-foreground focus:ring-2 focus:ring-primary/20 transition-all cursor-pointer"
              />
              {reduceOnlyEnabled && (
                <svg
                  className="absolute inset-0 w-4 h-4 text-foreground pointer-events-none"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M13 4L6 11L3 8"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </div>
            <span className="text-muted-foreground group-hover:text-foreground transition-colors">Reduce-Only</span>
          </label>
          
          {/* Time in Force Selector */}
          <TimeInForceSelector
            value={timeInForce}
            onChange={onTimeInForceChange}
            orderType={orderType}
          />
        </div>
        <label className="flex items-center gap-2 text-xs cursor-pointer group">
          <div className="relative">
            <input
              type="checkbox"
              checked={tpslEnabled}
              onChange={(e) => {
                const checked = e.target.checked;
                setTpslEnabled(checked);
                // If TP/SL is enabled, disable Reduce-Only
                if (checked) {
                  setReduceOnlyEnabled(false);
                }
              }}
              className="appearance-none w-4 h-4 rounded border border-muted-foreground/30 bg-muted checked:bg-muted checked:border-foreground focus:ring-2 focus:ring-primary/20 transition-all cursor-pointer"
            />
            {tpslEnabled && (
              <svg
                className="absolute inset-0 w-4 h-4 text-foreground pointer-events-none"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M13 4L6 11L3 8"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </div>
          <span className="text-muted-foreground group-hover:text-foreground transition-colors">Take Profit / Stop Loss</span>
        </label>
      </div>

      {/* TP/SL Expanded Section - Compact Side-by-Side */}
      {tpslEnabled && (
        <div className="flex flex-col gap-2 pt-0 animate-in slide-in-from-top-2">
          {/* Single row with Take Profit and Stop Loss side-by-side */}
          <div className="grid grid-cols-2 gap-2">
            {/* Take Profit Column */}
            <div>
              <label className="block text-xs font-normal text-foreground/80 mb-1.5">
                Take Profit
              </label>
              <TpSlInput
                value={tpPrice}
                onChange={handleTpPriceChange}
                onFocus={() => { activeInputRef.current = "tpPrice"; }}
                onBlur={() => { activeInputRef.current = null; }}
                onClear={() => setTpPrice("")}
                placeholder=""
                suffix="USD"
              />
              {estimatedPnL.estProfit !== null && estimatedPnL.estProfit > 0 && (
                <p className="text-xs text-bid-foreground/80 mt-1">
                  Profit: +${formatWithCommas(Math.abs(estimatedPnL.estProfit).toFixed(2))}
                </p>
              )}
            </div>

            {/* Stop Loss Column */}
            <div>
              <label className="block text-xs font-normal text-foreground/80 mb-1.5">
                Stop Loss
              </label>
              <TpSlInput
                value={slPrice}
                onChange={handleSlPriceChange}
                onFocus={() => { activeInputRef.current = "slPrice"; }}
                onBlur={() => { activeInputRef.current = null; }}
                onClear={() => setSlPrice("")}
                placeholder=""
                suffix="USD"
              />
              {estimatedPnL.estLoss !== null && estimatedPnL.estLoss > 0 && (
                <p className="text-xs text-ask-foreground/80 mt-1">
                  Loss: -${formatWithCommas(Math.abs(estimatedPnL.estLoss).toFixed(2))}
                </p>
              )}
            </div>
          </div>
          
        </div>
      )}
    </div>
  );
};

